import { type Session, type SessionV2 } from "@decentriq/core";
import { type FullDataLabFragment } from "@decentriq/graphql/dist/types";
import { type UseQueryResult } from "@tanstack/react-query";
import type JSZip from "jszip";
import { createContext } from "react";
import { type ValidationReport } from "validate-js";
import { type MediaDataRoomJobHookResult } from "features/mediaDataRoom/hooks";
import {
  type BaseAudienceDatasetsHashes,
  type LegacyValidationReport,
  type MediaDataRoomVersion,
  type OverlapSegment,
  type PublishedDatasetsHashes,
} from "features/mediaDataRoom/models";

export type DatasetLeafName =
  | "matching"
  | "segments"
  | "demographics"
  | "audiences"
  | "embeddings";

export type DatasetsResult = Omit<
  UseQueryResult<PublishedDatasetsHashes, Error>,
  "data"
> & {
  data: PublishedDatasetsHashes;
  updateAudiencesDatasetHash: (value: string | null) => void;
  updateAdvertiserDatasetHash: (value: string | null) => void;
  updatePublisherDatasetsHashes: (
    value: BaseAudienceDatasetsHashes | null
  ) => void;
  updatePublishedDatasetsHashes: (
    value: PublishedDatasetsHashes | null
  ) => void;
  // This is needed only for AB, TODO: potentially branch it
  fetch: () => Promise<PublishedDatasetsHashes>;
};

export interface InsightsData {
  insights: OverlapSegment[];
  segmentsRawFile: JSZip.JSZipObject;
}

export type InsightsResult = MediaDataRoomJobHookResult<InsightsData>;

export interface AudienceOverlapStatistics {
  advertiserSize: number;
  audienceType: string;
  overlapSize: number;
  isEmpty: boolean;
}

export interface OverlapStatisticsData {
  statistics: AudienceOverlapStatistics[];
  isEmpty: boolean;
}

export type OverlapStatisticsResult =
  MediaDataRoomJobHookResult<OverlapStatisticsData>;

export type AdvertiserDataReportResult = MediaDataRoomJobHookResult<
  ValidationReport | LegacyValidationReport
>;

export interface PublisherDataReportResult {
  matchingDataset: MediaDataRoomJobHookResult<ValidationReport>;
  demographicsDataset: MediaDataRoomJobHookResult<ValidationReport>;
  segmentsDataset: MediaDataRoomJobHookResult<ValidationReport>;
}

export interface MediaDataRoomInsightsDataContextValue {
  datasets: DatasetsResult;
  publishedDatasetsHashes: DatasetsResult["data"];
  isPublisherAudienceBeingUpdated: boolean;
  insights: InsightsResult;
  overlapStatistics: OverlapStatisticsResult;
  advertiserDataReport: AdvertiserDataReportResult;
  publisherDataReport: PublisherDataReportResult;
  publishedDataLab: FullDataLabFragment | null;
  dataLabDataLoading: boolean;
  version: MediaDataRoomVersion;
}

export interface MediaDataRoomInsightsDataAbContextValue
  extends MediaDataRoomInsightsDataContextValue {
  session?: Session;
  getScopeId: () => Promise<string>;
  resetScopeId: () => void;
}

export interface MediaDataRoomInsightsDataV3ContextValue
  extends MediaDataRoomInsightsDataContextValue {
  session?: SessionV2;
}

export interface MediaDataRoomInsightsDataContextHook {
  (version?: never): MediaDataRoomInsightsDataContextValue;
  (version: MediaDataRoomVersion.v3): MediaDataRoomInsightsDataV3ContextValue;
  (version: MediaDataRoomVersion.ab): MediaDataRoomInsightsDataAbContextValue;
}

export const MediaDataRoomInsightsDataContext =
  createContext<MediaDataRoomInsightsDataContextValue | null>(null);
MediaDataRoomInsightsDataContext.displayName =
  "MediaDataRoomInsightsDataContext";
